/**
 * Kho Idempotency-Key theo "phạm vi thao tác".
 * - Cùng một thao tác (vd. đặt cùng báo giá) ⇒ luôn dùng cùng key, kể cả khi bấm lại hoặc tải lại trang.
 * - Chỉ xoá key khi Backend đã trả kết quả cuối (thành công hoặc bị từ chối rõ ràng).
 */
export interface IdempotencyStore {
  /** Lấy key hiện có của scope, hoặc tạo mới nếu chưa có. */
  keyFor(scope: string): string;
  peek(scope: string): string | null;
  release(scope: string): void;
  clearAll(): void;
}

const PREFIX = 'gsm.idem.';

export const newKey = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export function createIdempotencyStore(storage: Storage | null = safeSession()): IdempotencyStore {
  const mem = new Map<string, string>();
  const read = (k: string) => {
    try {
      return storage?.getItem(PREFIX + k) ?? mem.get(k) ?? null;
    } catch {
      return mem.get(k) ?? null;
    }
  };
  const write = (k: string, v: string) => {
    mem.set(k, v);
    try {
      storage?.setItem(PREFIX + k, v);
    } catch {
      /* private mode: dùng bộ nhớ */
    }
  };
  return {
    keyFor(scope) {
      const existing = read(scope);
      if (existing) return existing;
      const k = newKey();
      write(scope, k);
      return k;
    },
    peek: read,
    release(scope) {
      mem.delete(scope);
      try {
        storage?.removeItem(PREFIX + scope);
      } catch {
        /* noop */
      }
    },
    clearAll() {
      mem.clear();
      try {
        if (!storage) return;
        Object.keys(storage)
          .filter((k) => k.startsWith(PREFIX))
          .forEach((k) => storage.removeItem(k));
      } catch {
        /* noop */
      }
    },
  };
}

function safeSession(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}
